import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  Box,
  CircularProgress,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";

import { styled, useTheme } from "@mui/material/styles";

import * as echarts from "echarts";
import { v4 as uuidv4 } from "uuid";

import useAuthScope from "hooks/useAuthScope";
import useDataSourceManager from "hooks/useDataSourceManager";

import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";

import CustomMenu from "components/Menu";

import mainStyle from "../../Dashboard/dashboard.module.scss";

const COLORS = {
  text: "#07164D",
  mutedText: "#8A96B3",
  border: "#DCE3F0",
  grid: "#E5E7EB",
  background: "#FFFFFF",
  softBackground: "#FAFBFD",
  selectedBackground: "#F5F7FC",
  throughput: "#1F3173",
  rssi: "#8CAAFB",
  clients24: "#1F3173",
  clients5: "#6688E8",
  clients6: "#B9C8F5",
  primary: "#1F3173",
};

const WEEK_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const TWO_POINT_FOUR_GHZ_RANGE = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14,
];

const FIVE_GHZ_RANGE = [
  36, 40, 44, 48, 52, 56, 60, 64, 100, 104, 108, 112, 124, 128, 132, 136, 140,
  144, 149, 153, 157, 161, 165,
];

const SIX_GHZ_RANGE = [
  1, 5, 9, 13, 17, 21, 25, 29, 33, 37, 41, 45, 49, 53, 57, 61, 65, 69, 73, 77,
  81, 85, 89, 93, 97, 101, 105, 109, 113, 117, 121, 125, 129, 133, 137, 141,
  145, 149, 153, 157, 161, 165, 169, 173, 177, 181, 185, 189, 193, 197, 201,
  205, 209, 213, 217, 221, 225, 229, 233,
];

const formatMbps = (value) => {
  const numberValue = Number(value);

  if (!Number.isFinite(numberValue)) {
    return "-";
  }

  return `${(numberValue / 1000000).toFixed(1)} Mbps`;
};

const formatBytes = (value) => {
  const numberValue = Number(value);

  if (!Number.isFinite(numberValue)) {
    return "-";
  }

  const KB = 1024;
  const MB = KB * 1024;
  const GB = MB * 1024;

  if (numberValue >= GB) {
    return `${(numberValue / GB).toFixed(1)} GB`;
  }

  if (numberValue >= MB) {
    return `${(numberValue / MB).toFixed(1)} MB`;
  }

  if (numberValue >= KB) {
    return `${(numberValue / KB).toFixed(1)} KB`;
  }

  return `${numberValue} B`;
};

const formatPercent = (value) => {
  const numberValue = Number(value);

  if (!Number.isFinite(numberValue)) {
    return "-";
  }

  return `${numberValue.toFixed(1)}%`;
};

const getWeekLabels = (data) => {
  return WEEK_DAYS.map((day, index) => {
    const item = data?.[index];

    if (!item) {
      return day;
    }

    if (item.label) {
      return item.label;
    }

    if (item.date) {
      return `${item.date} ${day}`;
    }

    if (item.day) {
      return `${item.day} ${day}`;
    }

    return day;
  });
};

const ChannelBox = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-end",
  paddingBottom: "6px",
  overflowX: "auto",
  overflowY: "hidden",
  marginTop: "5px",

  "&::-webkit-scrollbar": {
    height: 5,
  },

  "&::-webkit-scrollbar-thumb": {
    backgroundColor: theme.palette.primary.main,
    borderRadius: 8,
  },

  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: theme.palette.primary.mainHover,
    cursor: "ew-resize",
  },

  "&::-webkit-scrollbar-track": {
    backgroundColor: theme.palette.primary.greyThread,
    borderRadius: theme.shape.customBorderRadius?.[0] ?? 4,
  },

  "&::-webkit-scrollbar-button": {
    display: "none",
    width: 0,
    height: 0,
  },
}));

const ScrollTooltipBox = styled(Box)(({ theme }) => ({
  maxHeight: "110px",
  overflowY: "auto",
  paddingRight: "4px",

  "&::-webkit-scrollbar": {
    width: 5,
  },

  "&::-webkit-scrollbar-thumb": {
    backgroundColor: theme.palette.primary.main,
    borderRadius: 8,
  },

  "&::-webkit-scrollbar-track": {
    backgroundColor: theme.palette.primary.greyThread,
    borderRadius: theme.shape.customBorderRadius?.[0] ?? 4,
  },
}));

const PortField = ({ portStyle, total }) => {
  const theme = useTheme();

  const hasValue = Number(total) > 0;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 20,
        maxWidth: 30,
        height: 20,
        borderRadius: "2px",
        px: 1,

        backgroundColor: hasValue
          ? portStyle.selectBackgroundColor
          : portStyle.backgroundColor,

        color: theme.palette.primary.white,
        fontSize: theme.typography.customFontSize?.[0] ?? 10,
        lineHeight: 1.33,
        mx: 0.5,
        whiteSpace: "nowrap",
        cursor: "default",
      }}
    >
      {hasValue ? total : ""}
    </Box>
  );
};

const ChannelField = ({ channelTitle, channelData, portData }) => {
  const theme = useTheme();

  const portStyleSheet = {
    "2.4GHz": {
      backgroundColor: theme.palette.primary.orangeSecound ?? "#F6C78B",
      selectBackgroundColor: theme.palette.primary.orange ?? "#F29A2E",
    },

    "5GHz": {
      backgroundColor: theme.palette.primary.greenFourth ?? "#A8DFCF",
      selectBackgroundColor: theme.palette.primary.greenPrimary ?? "#42B883",
    },

    "6GHz": {
      backgroundColor: theme.palette.primary.lightBlue ?? "#B9C8F5",
      selectBackgroundColor: theme.palette.primary.main ?? "#1F3173",
    },
  };

  return (
    <Box sx={{ marginLeft: "22px" }}>
      <Typography
        sx={{
          color: theme.palette.primary.greyPrimary,
          fontSize: theme.typography.customFontSize?.[3] ?? 13,
          fontWeight: theme.typography.customFontWeight?.[1] ?? 600,
          lineHeight: 0.95,
          padding: "3px",
        }}
      >
        {channelTitle}
      </Typography>

      <ChannelBox>
        {channelData.map((channelNumber) => {
          const channelInfo = portData.find(
            (item) => Number(item.channel) === Number(channelNumber),
          );

          return (
            <Tooltip
              key={`${channelTitle}-${channelNumber}`}
              arrow
              title={
                channelInfo?.devices?.length > 0 ? (
                  <ScrollTooltipBox>
                    {channelInfo.devices.map((item, index) => (
                      <Typography
                        key={`${item.id}-${index}`}
                        sx={{
                          fontSize: theme.typography.customFontSize?.[1] ?? 11,
                          fontWeight:
                            theme.typography.customFontWeight?.[1] ?? 500,
                          lineHeight: 1.69,
                          color: theme.palette.primary.white,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item.name ?? item.id}
                      </Typography>
                    ))}
                  </ScrollTooltipBox>
                ) : (
                  ""
                )
              }
            >
              <Box
                sx={{
                  textAlign: "center",
                  mx: 0.5,
                  flex: "0 0 auto",
                  marginTop: "5px",
                }}
              >
                <Typography
                  sx={{
                    fontSize: theme.typography.customFontSize?.[0] ?? 10,
                    fontWeight: theme.typography.customFontWeight?.[0] ?? 400,
                    lineHeight: 1.33,
                    color: theme.palette.primary.greySixth,
                    mb: 0.5,
                    cursor: "default",
                  }}
                >
                  {channelNumber}
                </Typography>

                <PortField
                  portStyle={portStyleSheet[channelTitle]}
                  total={channelInfo?.total ?? 0}
                />
              </Box>
            </Tooltip>
          );
        })}
      </ChannelBox>
    </Box>
  );
};

const ApChannelUtilization = ({ sortable = false, data = [] }) => {
  const groupedData = useMemo(() => {
    const map = new Map();

    const bandMap = {
      "2.4G": "2.4GHz",
      "2.4GHz": "2.4GHz",
      "5G": "5GHz",
      "5GHz": "5GHz",
      "6G": "6GHz",
      "6GHz": "6GHz",
    };

    data.forEach((item, index) => {
      if (!item || !item.band || item.channel == null) {
        return;
      }

      const normalizedBand = bandMap[item.band];

      if (!normalizedBand) {
        return;
      }

      const channel = Number(item.channel);

      if (!Number.isFinite(channel)) {
        return;
      }

      const key = `${normalizedBand}-${channel}`;

      if (!map.has(key)) {
        map.set(key, {
          band: normalizedBand,
          channel,
          total: 0,
          devices: [],
        });
      }

      const target = map.get(key);

      target.total += 1;

      target.devices.push({
        id: item.apId ?? item.apName ?? `${key}-${index}`,
        name: item.apName ?? item.apId ?? "-",
      });
    });

    return Array.from(map.values());
  }, [data]);

  const twoPointFourGHzChannelData = useMemo(
    () => groupedData.filter((item) => item.band === "2.4GHz"),
    [groupedData],
  );

  const fiveGHzChannelData = useMemo(
    () => groupedData.filter((item) => item.band === "5GHz"),
    [groupedData],
  );

  const sixGHzChannelData = useMemo(
    () => groupedData.filter((item) => item.band === "6GHz"),
    [groupedData],
  );

  return (
    <div
      style={{
        minHeight: "250px",
      }}
      className={`main-card-content ${mainStyle["dndObj_card"]} ${
        sortable ? mainStyle["dndObj_edit"] : ""
      }`}
    >
      <div className={mainStyle["dndObj_title"]}>
        <span style={{ marginLeft: "10px" }}>
          Access Point Channel Map (Last Snapshot)
        </span>
      </div>

      <Grid container sx={{ marginTop: "10px" }}>
        <Grid item xs={4} sx={{ marginBottom: "15px" }}>
          <ChannelField
            channelTitle="2.4GHz"
            channelData={TWO_POINT_FOUR_GHZ_RANGE}
            portData={twoPointFourGHzChannelData}
          />
        </Grid>

        <Grid item xs={8} sx={{ marginBottom: "15px" }}>
          <ChannelField
            channelTitle="5GHz"
            channelData={FIVE_GHZ_RANGE}
            portData={fiveGHzChannelData}
          />
        </Grid>

        <Grid item xs={12}>
          <ChannelField
            channelTitle="6GHz"
            channelData={SIX_GHZ_RANGE}
            portData={sixGHzChannelData}
          />
        </Grid>
      </Grid>
    </div>
  );
};

const FilterButton = ({ value, onClick, open }) => {
  return (
    <Box
      onClick={onClick}
      sx={{
        width: "100%",
        height: "34px",
        px: "10px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        boxSizing: "border-box",
        border: open
          ? `1px solid ${COLORS.primary}`
          : `1px solid ${COLORS.border}`,
        borderRadius: "6px",
        backgroundColor: "#FFFFFF",
        cursor: "pointer",
        transition: "0.15s",

        "&:hover": {
          borderColor: COLORS.primary,
        },
      }}
    >
      <Typography
        sx={{
          flex: 1,
          minWidth: 0,
          fontSize: "12px",
          color: COLORS.text,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {value}
      </Typography>

      <Box
        component="span"
        sx={{
          marginLeft: "8px",
          width: 0,
          height: 0,
          borderLeft: "4px solid transparent",
          borderRight: "4px solid transparent",
          borderTop: open ? "none" : "5px solid #8A96B3",
          borderBottom: open ? "5px solid #8A96B3" : "none",
          flexShrink: 0,
        }}
      />
    </Box>
  );
};

const WirelessInsight = ({ timeRange }) => {
  const { auth, standalone, isAuthReady } = useAuthScope();

  const { sendBatch } = useDataSourceManager();

  const [ssid, setSsid] = useState("All");
  const [apId, setApId] = useState("All");

  const [ssidOptions, setSsidOptions] = useState([]);
  const [apOptions, setApOptions] = useState([]);

  const [ssidAnchorEl, setSsidAnchorEl] = useState(null);
  const [apAnchorEl, setApAnchorEl] = useState(null);

  const [clientThroughput, setClientThroughput] = useState([]);
  const [clientSeries, setClientSeries] = useState([]);
  const [clientList, setClientList] = useState([]);
  const [channelMap, setChannelMap] = useState([]);

  const [selectedClientId, setSelectedClientId] = useState(null);

  const [loading, setLoading] = useState(true);

  const chart1Ref = useRef(null);
  const chart2Ref = useRef(null);

  const chart1InstanceRef = useRef(null);
  const chart2InstanceRef = useRef(null);

  const summaryReportId = timeRange?.summaryReportId;

  const entId = standalone ? null : auth.realm?.id;
  const orgId = standalone ? auth.realm?.id : auth.orgs?.id;
  const siteId = auth.sites?.id;

  useEffect(() => {
    if (!isAuthReady) {
      return;
    }

    if (!summaryReportId) {
      setClientThroughput([]);
      setClientSeries([]);
      setClientList([]);
      setChannelMap([]);
      setSsidOptions([]);
      setApOptions([]);
      setSelectedClientId(null);
      setLoading(false);

      return;
    }

    if (!orgId || !siteId) {
      console.warn(">>> [WirelessInsight] missing id", {
        orgId,
        siteId,
      });

      setLoading(false);

      return;
    }

    const apiKey = standalone
      ? "/orgs/:orgId/sites/:siteId/summaryReports/:summaryReportId/wirelessInsight.read"
      : "/ents/:entId/orgs/:orgId/sites/:siteId/summaryReports/:summaryReportId/wirelessInsight.read";

    const restfulPlaceholders = standalone
      ? {
          orgId,
          siteId,
          summaryReportId,
        }
      : {
          entId,
          orgId,
          siteId,
          summaryReportId,
        };

    const load = async () => {
      const requestUuid = uuidv4();

      try {
        setLoading(true);

        const params = {
          data: {
            ssid: ssid === "All" ? "" : ssid,
            apId: apId === "All" ? "" : apId,
          },
        };

        const { getSendBatchData } = await sendBatch([
          {
            apiKey,
            requestUuid,
            restfulPlaceholders,
            params,
          },
        ]);

        const response = getSendBatchData({
          requestUuid,
        });

        const data =
          response?.result?.data ??
          response?.data ??
          {};

        const filters = data?.filters ?? {};
        setSsidOptions(
          Array.isArray(filters.ssids)
            ? filters.ssids
            : [],
        );

        setApOptions(
          Array.isArray(filters.aps)
            ? filters.aps
            : [],
        );

        const nextClientThroughput =
          Array.isArray(data.clientThroughput)
            ? data.clientThroughput
                .map((item) => {
                  if (!item) {
                    return null;
                  }

                  return {
                    ...item,

                    throughput: Number(
                      item?.throughput ?? 0,
                    ),

                    clientCount: Number(
                      item?.clientCount ?? 0,
                    ),

                    clients2g: Number(
                      item?.clients2g ??
                        item?.clients24g ??
                        item?.client2g ??
                        0,
                    ),

                    clients5g: Number(
                      item?.clients5g ??
                        item?.client5g ??
                        0,
                    ),

                    clients6g: Number(
                      item?.clients6g ??
                        item?.client6g ??
                        0,
                    ),
                  };
                })
                .filter(Boolean)
            : [];

        setClientThroughput(
          nextClientThroughput,
        );

        const nextClientSeries =
          Array.isArray(data.clientSeries)
            ? data.clientSeries.map(
                (client) => ({
                  ...client,

                  daily: Array.isArray(
                    client?.daily,
                  )
                    ? client.daily
                        .map((item) => {
                          if (!item) {
                            return null;
                          }

                          return {
                            throughput:
                              item?.throughput !=
                              null
                                ? Number(
                                    item.throughput,
                                  )
                                : null,

                            rssiDbm:
                              item?.rssiDbm !=
                              null
                                ? Number(
                                    item.rssiDbm,
                                  )
                                : null,
                          };
                        })
                        .filter(Boolean)
                    : [],
                }),
              )
            : [];

        setClientSeries(
          nextClientSeries,
        );

        const nextClientList =
          Array.isArray(data.clientList)
            ? data.clientList.map(
                (client) => ({
                  ...client,

                  rssiDbm:
                    client?.rssiDbm != null
                      ? Number(
                          client.rssiDbm,
                        )
                      : null,

                  throughputLast: Number(
                    client?.throughputLast ??
                      0,
                  ),

                  throughputP95: Number(
                    client?.throughputP95 ??
                      0,
                  ),

                  throughputActiveAvg:
                    Number(
                      client?.throughputActiveAvg ??
                        0,
                    ),

                  totalTrafficBytes:
                    Number(
                      client?.totalTrafficBytes ??
                        0,
                    ),

                  usagePercent: Number(
                    client?.usagePercent ??
                      0,
                  ),
                }),
              )
            : [];

        setClientList(nextClientList);
        setSelectedClientId(
          (previousId) => {
            if (
              nextClientList.length === 0
            ) {
              return null;
            }

            const stillExists =
              nextClientList.some(
                (client) =>
                  client.clientId ===
                  previousId,
              );

            if (stillExists) {
              return previousId;
            }

            return nextClientList[0]
              .clientId;
          },
        );
        const nextChannelMap =
          Array.isArray(
            data?.channelMap?.items,
          )
            ? data.channelMap.items.map(
                (item) => ({
                  ...item,

                  channel:
                    item?.channel != null
                      ? Number(
                          item.channel,
                        )
                      : null,
                }),
              )
            : [];

        setChannelMap(nextChannelMap);
      } catch (error) {
        console.error(
          ">>> [WirelessInsight] load error:",
          error,
        );

        setClientThroughput([]);
        setClientSeries([]);
        setClientList([]);
        setChannelMap([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [
    apId,
    entId,
    isAuthReady,
    orgId,
    sendBatch,
    siteId,
    ssid,
    standalone,
    summaryReportId,
  ]);

  useEffect(() => {
    setSelectedClientId(null);
  }, [summaryReportId]);

  const selectedClient = useMemo(() => {
    if (!selectedClientId) {
      return null;
    }

    return (
      clientList.find((client) => client.clientId === selectedClientId) ?? null
    );
  }, [clientList, selectedClientId]);

  const selectedClientSeries = useMemo(() => {
    if (!selectedClientId) {
      return null;
    }

    return (
      clientSeries.find((item) => item.clientId === selectedClientId) ?? null
    );
  }, [clientSeries, selectedClientId]);

  const currentApName = useMemo(() => {
    if (apId === "All") {
      return "All";
    }

    return apOptions.find((item) => item.apId === apId)?.apName ?? "All";
  }, [apId, apOptions]);


  const wirelessVsThroughputOption = useMemo(() => {
    const labels = getWeekLabels(clientThroughput);

    const throughputData = WEEK_DAYS.map((_, index) => {
      const item = clientThroughput[index];

      if (!item) {
        return null;
      }

      return Number(item.throughput ?? 0) / 1000000;
    });

    const hasBandData = clientThroughput.some(
      (item) =>
        item &&
        (Number(item.clients2g) > 0 ||
          Number(item.clients5g) > 0 ||
          Number(item.clients6g) > 0),
    );

    const clients24Data = WEEK_DAYS.map((_, index) => {
      const item = clientThroughput[index];

      if (!item) {
        return null;
      }

      return hasBandData
        ? Number(item.clients2g ?? 0)
        : Number(item.clientCount ?? 0);
    });

    const clients5Data = WEEK_DAYS.map((_, index) => {
      const item = clientThroughput[index];

      if (!item) {
        return null;
      }

      return hasBandData ? Number(item.clients5g ?? 0) : 0;
    });

    const clients6Data = WEEK_DAYS.map((_, index) => {
      const item = clientThroughput[index];

      if (!item) {
        return null;
      }

      return hasBandData ? Number(item.clients6g ?? 0) : 0;
    });

    const stackedClients = clients24Data.map((value, index) => {
      const value24 = Number.isFinite(value) ? value : 0;

      const value5 = Number.isFinite(clients5Data[index])
        ? clients5Data[index]
        : 0;

      const value6 = Number.isFinite(clients6Data[index])
        ? clients6Data[index]
        : 0;

      return value24 + value5 + value6;
    });

    const validClientValues = stackedClients.filter(Number.isFinite);

    const maxClients = validClientValues.length
      ? Math.max(...validClientValues)
      : 0;

    const validThroughput = throughputData.filter(Number.isFinite);

    const maxThroughput = validThroughput.length
      ? Math.max(...validThroughput)
      : 0;

    const clientAxisMax = Math.max(10, Math.ceil(maxClients / 10) * 10);

    const throughputAxisMax = Math.max(10, Math.ceil(maxThroughput / 10) * 10);

    return {
      animationDuration: 400,

      tooltip: {
        trigger: "axis",

        backgroundColor: "#FFFFFF",
        borderColor: COLORS.border,
        borderWidth: 1,

        textStyle: {
          color: COLORS.text,
          fontSize: 11,
        },

        axisPointer: {
          type: "line",

          lineStyle: {
            color: "#BBBBBB",
          },
        },
      },

      legend: {
        bottom: 8,
        left: "center",
        itemWidth: 12,
        itemHeight: 7,
        itemGap: 15,

        textStyle: {
          fontSize: 10,
          color: "#666666",
        },

        data: ["Throughput (Mbps)", "Clients 2.4G", "Clients 5G", "Clients 6G"],
      },

      grid: {
        left: 50,
        right: 50,
        top: 45,
        bottom: 65,
      },

      xAxis: {
        type: "category",
        data: labels,

        axisTick: {
          show: false,
        },

        axisLine: {
          show: true,

          lineStyle: {
            color: "#CCCCCC",
          },
        },

        axisLabel: {
          color: "#666666",
          fontSize: 9,
          interval: 0,
        },

        splitLine: {
          show: true,

          lineStyle: {
            color: COLORS.grid,
            width: 1,
          },
        },
      },

      yAxis: [
        {
          type: "value",
          name: "Clients",
          min: 0,
          max: clientAxisMax,
          splitNumber: 5,
          nameGap: 10,

          nameTextStyle: {
            color: "#999999",
            fontSize: 9,
          },

          axisLabel: {
            color: "#999999",
            fontSize: 9,
          },

          axisLine: {
            show: false,
          },

          axisTick: {
            show: false,
          },

          splitLine: {
            show: true,

            lineStyle: {
              color: COLORS.grid,
              width: 1,
            },
          },
        },

        {
          type: "value",
          name: "Mbps",
          min: 0,
          max: throughputAxisMax,
          splitNumber: 4,
          nameGap: 10,

          nameTextStyle: {
            color: "#999999",
            fontSize: 9,
          },

          axisLabel: {
            color: "#999999",
            fontSize: 9,
          },

          axisLine: {
            show: false,
          },

          axisTick: {
            show: false,
          },

          splitLine: {
            show: false,
          },
        },
      ],

      series: [
        {
          name: "Clients 2.4G",
          type: "bar",
          stack: "clients",
          data: clients24Data,
          barWidth: 28,

          itemStyle: {
            color: COLORS.clients24,
          },

          emphasis: {
            disabled: true,
          },
        },

        {
          name: "Clients 5G",
          type: "bar",
          stack: "clients",
          data: clients5Data,
          barWidth: 28,

          itemStyle: {
            color: COLORS.clients5,
          },

          emphasis: {
            disabled: true,
          },
        },

        {
          name: "Clients 6G",
          type: "bar",
          stack: "clients",
          data: clients6Data,
          barWidth: 28,

          itemStyle: {
            color: COLORS.clients6,
          },

          emphasis: {
            disabled: true,
          },
        },

        {
          name: "Throughput (Mbps)",
          type: "line",
          yAxisIndex: 1,
          data: throughputData,
          smooth: false,
          showSymbol: true,
          symbol: "circle",
          symbolSize: 6,
          connectNulls: false,

          lineStyle: {
            color: COLORS.throughput,
            width: 2,
          },

          itemStyle: {
            color: COLORS.throughput,
          },

          z: 20,
        },
      ],
    };
  }, [clientThroughput]);

  const throughputRssiOption = useMemo(() => {
    const daily = selectedClientSeries?.daily ?? [];

    const normalizedDaily = WEEK_DAYS.map((_, index) => daily[index] ?? null);

    const throughputData = normalizedDaily.map((item) =>
      item?.throughput != null ? Number(item.throughput) / 1000000 : null,
    );

    const rssiData = normalizedDaily.map((item) =>
      item?.rssiDbm != null ? Number(item.rssiDbm) : null,
    );

    const validThroughput = throughputData.filter(Number.isFinite);

    const maxThroughput = validThroughput.length
      ? Math.max(...validThroughput)
      : 0;

    const throughputAxisMax = Math.max(20, Math.ceil(maxThroughput / 20) * 20);

    return {
      animationDuration: 400,

      tooltip: {
        trigger: "axis",
        backgroundColor: "#FFFFFF",
        borderColor: COLORS.border,
        borderWidth: 1,

        textStyle: {
          color: COLORS.text,
          fontSize: 11,
        },

        axisPointer: {
          type: "line",

          lineStyle: {
            color: "#BBBBBB",
          },
        },

        formatter: (params) => {
          if (!Array.isArray(params) || params.length === 0) {
            return "";
          }

          const day = params[0]?.axisValue ?? "";

          const content = params
            .filter((item) => item.value != null)
            .map((item) => {
              if (item.seriesName === "RSSI (dBm)") {
                return `${item.marker} ${item.seriesName}: ${item.value} dBm`;
              }

              return `${item.marker} ${item.seriesName}: ${item.value} Mbps`;
            })
            .join("<br/>");

          return content ? `${day}<br/>${content}` : day;
        },
      },

      legend: {
        bottom: 8,
        left: "center",
        itemWidth: 18,
        itemHeight: 3,
        itemGap: 20,

        textStyle: {
          fontSize: 10,
          color: "#666666",
        },

        data: ["Throughput (Mbps)", "RSSI (dBm)"],
      },

      grid: {
        left: 50,
        right: 50,
        top: 45,
        bottom: 65,
      },

      xAxis: {
        type: "category",
        data: WEEK_DAYS,
        boundaryGap: false,

        axisTick: {
          show: false,
        },

        axisLine: {
          show: true,

          lineStyle: {
            color: "#CCCCCC",
          },
        },

        axisLabel: {
          fontSize: 9,
          color: "#666666",
          interval: 0,
        },

        splitLine: {
          show: true,

          lineStyle: {
            color: COLORS.grid,
            width: 1,
          },
        },
      },

      yAxis: [
        {
          type: "value",
          name: "Mbps",
          min: 0,
          max: throughputAxisMax,
          splitNumber: 5,
          nameGap: 10,

          nameTextStyle: {
            color: "#888888",
            fontSize: 9,
          },

          axisLabel: {
            color: "#888888",
            fontSize: 9,
          },

          axisLine: {
            show: false,
          },

          axisTick: {
            show: false,
          },

          splitLine: {
            show: true,

            lineStyle: {
              color: COLORS.grid,
              width: 1,
            },
          },
        },

        {
          type: "value",
          name: "RSSI (dBm)",
          min: -90,
          max: -40,
          interval: 10,
          nameLocation: "start",
          nameGap: 10,

          nameTextStyle: {
            color: "#888888",
            fontSize: 9,
          },

          axisLabel: {
            color: "#888888",
            fontSize: 9,
          },

          axisLine: {
            show: false,
          },

          axisTick: {
            show: false,
          },

          splitLine: {
            show: false,
          },
        },
      ],

      series: [
        {
          name: "Throughput (Mbps)",
          type: "line",
          yAxisIndex: 0,
          data: throughputData,
          smooth: 0.35,
          showSymbol: true,
          symbol: "circle",
          symbolSize: 6,
          connectNulls: false,

          lineStyle: {
            color: COLORS.throughput,
            width: 2,
          },

          itemStyle: {
            color: COLORS.throughput,
          },

          z: 10,
        },

        {
          name: "RSSI (dBm)",
          type: "line",
          yAxisIndex: 1,
          data: rssiData,
          smooth: 0.35,
          showSymbol: true,
          symbol: "circle",
          symbolSize: 5,
          connectNulls: false,

          lineStyle: {
            color: COLORS.rssi,
            width: 2,
          },

          itemStyle: {
            color: COLORS.rssi,
          },

          z: 9,
        },
      ],
    };
  }, [selectedClientSeries]);

  /**
   * Chart 1 init
   */
  useEffect(() => {
    if (!chart1Ref.current) {
      return;
    }

    if (!chart1InstanceRef.current) {
      chart1InstanceRef.current = echarts.init(chart1Ref.current);
    }

    chart1InstanceRef.current.setOption(wirelessVsThroughputOption, true);

    const resize = () => {
      chart1InstanceRef.current?.resize();
    };

    window.addEventListener("resize", resize);

    return () => {
      window.removeEventListener("resize", resize);
    };
  }, [wirelessVsThroughputOption]);

  useEffect(() => {
    if (!chart2Ref.current) {
      return;
    }

    if (!chart2InstanceRef.current) {
      chart2InstanceRef.current = echarts.init(chart2Ref.current);
    }

    chart2InstanceRef.current.setOption(throughputRssiOption, true);

    const resize = () => {
      chart2InstanceRef.current?.resize();
    };

    window.addEventListener("resize", resize);

    return () => {
      window.removeEventListener("resize", resize);
    };
  }, [throughputRssiOption]);

  useEffect(() => {
    return () => {
      chart1InstanceRef.current?.dispose();
      chart2InstanceRef.current?.dispose();

      chart1InstanceRef.current = null;
      chart2InstanceRef.current = null;
    };
  }, []);

  const handleOpenSsidMenu = (event) => {
    event.stopPropagation();

    setSsidAnchorEl(event.currentTarget);
    setApAnchorEl(null);
  };

  const handleCloseSsidMenu = () => {
    setSsidAnchorEl(null);
  };

  const handleOpenApMenu = (event) => {
    event.stopPropagation();

    setApAnchorEl(event.currentTarget);
    setSsidAnchorEl(null);
  };

  const handleCloseApMenu = () => {
    setApAnchorEl(null);
  };

  const handleSsidChange = (value) => {
    setSelectedClientId(null);
    setSsid(value);
    setSsidAnchorEl(null);
  };

  const handleApChange = (value) => {
    setSelectedClientId(null);
    setApId(value);
    setApAnchorEl(null);
  };

  const handleSelectClient = (clientId) => {
    setSelectedClientId(clientId);
  };

  const handleClear = () => {
    setSelectedClientId(null);
    setSsid("All");
    setApId("All");
  };

  return (
    <Box
      sx={{
        width: "100%",
        boxSizing: "border-box",
        backgroundColor: "#FFFFFF",
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          gap: "16px",
          boxSizing: "border-box",
          backgroundColor: "#FFFFFF",
        }}
      >
        {/* LEFT */}
        <Box
          sx={{
            width: "43%",
            minWidth: "400px",
            padding: "16px",
            border: `1px solid ${COLORS.border}`,
            borderRadius: "6px",
            backgroundColor: "#FFFFFF",
            boxSizing: "border-box",
          }}
        >
          {/* FILTER */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              marginBottom: "18px",
            }}
          >
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 500,
                color: COLORS.text,
                whiteSpace: "nowrap",
              }}
            >
              SSID
            </Typography>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <FilterButton
                value={ssid}
                open={Boolean(ssidAnchorEl)}
                onClick={handleOpenSsidMenu}
              />

              <CustomMenu
                anchorEl={ssidAnchorEl}
                handleMenuClose={handleCloseSsidMenu}
                mt={0.5}
                minWidth="180px"
                maxWidth="260px"
                anchorHorizontal="left"
                transformHorizontal="left"
              >
                <List
                  disablePadding
                  sx={{
                    py: "4px",
                    maxHeight: "280px",
                    overflowY: "auto",
                  }}
                >
                  <ListItem
                    onClick={() => handleSsidChange("All")}
                    sx={{
                      minHeight: "36px",
                      px: "12px",
                      cursor: "pointer",
                      backgroundColor:
                        ssid === "All"
                          ? COLORS.selectedBackground
                          : "transparent",

                      "&:hover": {
                        backgroundColor: "#F5F7FC",
                      },
                    }}
                  >
                    <ListItemText
                      primary="All"
                      primaryTypographyProps={{
                        fontSize: "12px",
                        fontWeight: ssid === "All" ? 600 : 400,
                        color: ssid === "All" ? COLORS.primary : COLORS.text,
                      }}
                    />
                  </ListItem>

                  {ssidOptions.map((item) => {
                    const selected = ssid === item;

                    return (
                      <ListItem
                        key={item}
                        onClick={() => handleSsidChange(item)}
                        sx={{
                          minHeight: "36px",
                          px: "12px",
                          cursor: "pointer",
                          backgroundColor: selected
                            ? COLORS.selectedBackground
                            : "transparent",

                          "&:hover": {
                            backgroundColor: "#F5F7FC",
                          },
                        }}
                      >
                        <ListItemText
                          primary={item}
                          primaryTypographyProps={{
                            fontSize: "12px",
                            fontWeight: selected ? 600 : 400,
                            color: selected ? COLORS.primary : COLORS.text,
                            noWrap: true,
                          }}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </CustomMenu>
            </Box>

            <Typography
              sx={{
                marginLeft: "8px",
                fontSize: "12px",
                fontWeight: 500,
                color: COLORS.text,
                whiteSpace: "nowrap",
              }}
            >
              AP
            </Typography>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <FilterButton
                value={currentApName}
                open={Boolean(apAnchorEl)}
                onClick={handleOpenApMenu}
              />

              <CustomMenu
                anchorEl={apAnchorEl}
                handleMenuClose={handleCloseApMenu}
                mt={0.5}
                minWidth="190px"
                maxWidth="280px"
                anchorHorizontal="left"
                transformHorizontal="left"
              >
                <List
                  disablePadding
                  sx={{
                    py: "4px",
                    maxHeight: "280px",
                    overflowY: "auto",
                  }}
                >
                  <ListItem
                    onClick={() => handleApChange("All")}
                    sx={{
                      minHeight: "36px",
                      px: "12px",
                      cursor: "pointer",
                      backgroundColor:
                        apId === "All"
                          ? COLORS.selectedBackground
                          : "transparent",

                      "&:hover": {
                        backgroundColor: "#F5F7FC",
                      },
                    }}
                  >
                    <ListItemText
                      primary="All"
                      primaryTypographyProps={{
                        fontSize: "12px",
                        fontWeight: apId === "All" ? 600 : 400,
                        color: apId === "All" ? COLORS.primary : COLORS.text,
                      }}
                    />
                  </ListItem>

                  {apOptions.map((item) => {
                    const selected = apId === item.apId;

                    return (
                      <ListItem
                        key={item.apId}
                        onClick={() => handleApChange(item.apId)}
                        sx={{
                          minHeight: "36px",
                          px: "12px",
                          cursor: "pointer",
                          backgroundColor: selected
                            ? COLORS.selectedBackground
                            : "transparent",

                          "&:hover": {
                            backgroundColor: "#F5F7FC",
                          },
                        }}
                      >
                        <ListItemText
                          primary={item.apName ?? item.apId}
                          primaryTypographyProps={{
                            fontSize: "12px",
                            fontWeight: selected ? 600 : 400,
                            color: selected ? COLORS.primary : COLORS.text,
                            noWrap: true,
                          }}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </CustomMenu>
            </Box>
          </Box>

          <Typography
            sx={{
              marginBottom: "12px",
              fontSize: "14px",
              fontWeight: 600,
              color: COLORS.text,
            }}
          >
            Client List (Connected this week)
          </Typography>

          <Box
            sx={{
              height: "585px",
              padding: "10px",
              overflowY: "auto",
              border: `1px solid ${COLORS.border}`,
              borderRadius: "4px",
              backgroundColor: COLORS.softBackground,
            }}
          >
            {loading ? (
              <Box
                sx={{
                  height: "180px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress size={24} />
              </Box>
            ) : clientList.length === 0 ? (
              <Box
                sx={{
                  height: "180px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: COLORS.mutedText,
                  }}
                >
                  No data available
                </Typography>
              </Box>
            ) : (
              clientList.map((client) => {
                const selected = selectedClientId === client.clientId;

                return (
                  <Box
                    key={client.clientId}
                    onClick={() => handleSelectClient(client.clientId)}
                    sx={{
                      marginBottom: "10px",
                      padding: "14px",
                      cursor: "pointer",
                      border: selected
                        ? `1px solid ${COLORS.primary}`
                        : `1px solid ${COLORS.border}`,
                      borderRadius: "5px",
                      backgroundColor: selected
                        ? COLORS.selectedBackground
                        : "#FFFFFF",
                      transition: "0.15s",

                      "&:hover": {
                        borderColor: COLORS.primary,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: "16px",
                      }}
                    >
                      <Box
                        sx={{
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "13px",
                            fontWeight: 600,
                            color: COLORS.text,
                          }}
                        >
                          {client.clientName ?? client.macAddress ?? "-"}
                        </Typography>

                        <Typography
                          sx={{
                            marginTop: "3px",
                            fontSize: "11px",
                            color: COLORS.mutedText,
                          }}
                        >
                          {client.macAddress ?? "-"}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          textAlign: "right",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "11px",
                            color: COLORS.text,
                          }}
                        >
                          Most used: {client.band ?? "-"} / {client.ssid ?? "-"}{" "}
                          · {formatPercent(client.usagePercent)}
                        </Typography>

                        <Typography
                          sx={{
                            marginTop: "4px",
                            fontSize: "11px",
                            color: COLORS.mutedText,
                          }}
                        >
                          RSSI (Last):{" "}
                          {client.rssiDbm != null
                            ? `${client.rssiDbm} dBm`
                            : "-"}
                        </Typography>
                      </Box>
                    </Box>

                    <Divider
                      sx={{
                        marginY: "10px",
                      }}
                    />

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-end",
                        gap: "16px",
                      }}
                    >
                      <Box>
                        <ClientInfo
                          title="Throughput (Last)"
                          value={formatMbps(client.throughputLast)}
                        />

                        <ClientInfo
                          title="Total traffic (Week, sum)"
                          value={formatBytes(client.totalTrafficBytes)}
                        />

                        <ClientInfo
                          title="Throughput (p95 / active-avg)"
                          value={`${formatMbps(
                            client.throughputP95,
                          )} / ${formatMbps(client.throughputActiveAvg)}`}
                        />
                      </Box>

                      <SolidButton
                        disableElevation
                        onClick={(event) => {
                          event.stopPropagation();

                          handleSelectClient(client.clientId);
                        }}
                        sx={{
                          height: "32px",
                        }}
                      >
                        View
                      </SolidButton>
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
        </Box>

        {/* RIGHT */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              height: "40px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "8px",
            }}
          >
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 500,
                color: COLORS.text,
              }}
            >
              {selectedClient
                ? `Viewing ${
                    selectedClient.clientName ?? selectedClient.macAddress
                  }`
                : "Select a client on the left to view details."}
            </Typography>

            <OutlineButton
              onClick={handleClear}
              sx={{
                height: "32px",
              }}
            >
              Clear
            </OutlineButton>
          </Box>

          <ChartCard title="Wireless Client vs Throughput">
            <Box
              ref={chart1Ref}
              sx={{
                width: "100%",
                height: "300px",
              }}
            />
          </ChartCard>

          <ChartCard title="Throughput & RSSI">
            <Box
              ref={chart2Ref}
              sx={{
                width: "100%",
                height: "275px",
              }}
            />
          </ChartCard>
        </Box>
      </Box>

      <Box
        sx={{
          width: "100%",
          marginTop: "16px",
          boxSizing: "border-box",
        }}
      >
        <ApChannelUtilization sortable={false} data={channelMap} />
      </Box>
    </Box>
  );
};

const ClientInfo = ({ title, value }) => {
  return (
    <Box
      sx={{
        marginBottom: "8px",
      }}
    >
      <Typography
        sx={{
          fontSize: "11px",
          color: COLORS.mutedText,
        }}
      >
        {title}
      </Typography>

      <Typography
        sx={{
          marginTop: "2px",
          fontSize: "13px",
          fontWeight: 600,
          color: COLORS.primary,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

const ChartCard = ({ title, children }) => {
  return (
    <Box
      sx={{
        width: "100%",
        marginBottom: "12px",
        overflow: "hidden",
        border: `1px solid ${COLORS.border}`,
        borderRadius: "6px",
        backgroundColor: "#FFFFFF",
      }}
    >
      <Typography
        sx={{
          paddingTop: "14px",
          paddingX: "16px",
          fontSize: "13px",
          fontWeight: 600,
          color: COLORS.text,
        }}
      >
        {title}
      </Typography>

      {children}
    </Box>
  );
};

export default WirelessInsight;
